/**
 * What the shell hands the stage's tenants (COMBAT.app, the ENCOUNTERS page): the scene this client
 * shows, the GM's private preview and its verbs (`decisions/0203` point 7), and the COMMS column's
 * body element. COMBAT.app portals its own COMMS into that element, so its decision cards keep the
 * live encounter socket and the shell never shows two feeds.
 *
 * It also carries the shell's dice opener, so COMBAT's roll buttons open the DICE rail page for a
 * player (`website-shell` 3.3) and seed the COMMS dice composer for the GM (`dice-rework` 1.2).
 *
 * Undefined outside the shell: a COMBAT.app mounted on its own keeps its inline COMMS sidebar and
 * has no dice.
 */
import { createContext, useContext } from "react";
import type { TableScene } from "@lancer-vtt/domain";
import type { PreselectBonus } from "../DiceApp";

export type StageControls = {
  /** The scene this client's stage shows: the preview, else the table's (the outgoing one until a cut is black). */
  shown: TableScene;
  /** The encounter this client previews, or null. GM only. */
  preview: string | null;
  /** *Peek*: show an encounter on this client's stage only (the table's own scene ends the preview). */
  peek: (encounterId: string) => void;
  /** RETURN: drop the preview; the stage shows the table's scene again. */
  endPreview: () => void;
  /** *Put on table*: make a scene the table's (and end any preview). False when not sent. */
  putOnTable: (scene: TableScene) => boolean;
  /** The COMMS column's body, for COMBAT.app's portal; null until it mounts. */
  commsSlot: HTMLElement | null;
  /** Open the viewer's dice roller, seeded when a stat cell asks (`useRailDice`). */
  openDice: (preselect?: PreselectBonus) => void;
  /** The GM's latest seed for the COMMS dice composer (`CommsDice`); null for a player. */
  commsDice: PreselectBonus | null;
  /** The stage's scene has drawn its first frame: lifts a scene cut's title card (`SceneCut.tsx`). */
  sceneReady: () => void;
};

export const StageContext = createContext<StageControls | undefined>(undefined);

export function useStage(): StageControls | undefined {
  return useContext(StageContext);
}
